import _ from 'lodash';

import { bindServices } from '@/services/api';
import { getFiltersGlobalFilters, toPaginate } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';
import { CoreEnums } from '@/utils/domain/constants';

const { BulkActionType } = CoreEnums;

const api = bindServices('userDevices');

const namespace = 'userDevices';
const fetchRoute = 'userDevice';
const fetchParams = 'UserDevice';

const initialPagination = { page: 1, count: 0, totalPages: 0 };

const initialState = {
  list: [],
  pagination: initialPagination,
  orderBy: '',
  select: CoreQueries[fetchParams].paged.select,
  showForm: false,
  detail: false,
  id: null,
  item: null,
  success: false,
};

const UserDevicesModel = {
  namespace,
  state: initialState,

  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const pageSize = yield select((state) => state.global.pageSize);
        const params = {
          ...CoreQueries[fetchParams].paged,
          pageSize,
          ...payload,
        };
        const res = yield call(api.getAll, {
          route: `${fetchRoute}/paged`,
          params,
          readPriority: true,
        });
        const items = res?.items || res?.data || [];
        const page = payload.page ?? 1;
        const limit = payload.limit ?? pageSize;
        const totalCount = res?.totalCount ?? res?.count ?? res?.total ?? items.length;
        yield put({
          type: 'queryData',
          payload: {
            ...res,
            limit,
            page,
            totalCount,
            items,
          },
        });
      } catch (e) {
        /*  */
      }
    },

    *fetchItem({ payload }, { call, put }) {
      try {
        yield put({ type: 'success', payload: false });
        const res = yield call(api.getById, {
          route: fetchRoute,
          id: payload.id,
          params: CoreQueries[fetchParams].itemDetail,
        });
        const item = Array.isArray(res) ? res[0] : res;
        yield put({ type: 'queryItem', payload: item });
      } catch (e) {
        /*  */
      }
    },

    *fetchDelete({ payload }, { call, put }) {
      try {
        const { id, view } = payload;
        yield put({
          type: 'global/modalConfirm',
          payload: { state: false, id: null, modelDelete: null },
        });
        if (view) {
          yield put({
            type: 'queryShowForm',
            payload: {
              state: false,
              item: null,
              detail: false,
              id: null,
            },
          });
        }
        yield call(api.remove, { route: fetchRoute, id });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *setDeviceLocked({ payload }, { call, put, select }) {
      try {
        const { id, locked } = payload;
        yield call(api.exec, {
          route: `${fetchRoute}/lockUnlock/${id}/${locked}`,
          httpMethod: 'post',
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        const pageSize = yield select((state) => state.global.pageSize);
        const deviceState = yield select((state) => state.userDevices);
        const globalState = yield select((state) => state.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const page = deviceState.pagination?.page ?? 1;
        const params = {
          ...CoreQueries[fetchParams].paged,
          page,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(deviceState.orderBy && { orderBy: deviceState.orderBy }),
          ...(filters && { filter: filters }),
          select: deviceState.select,
        };
        yield put({ type: 'fetchList', payload: params });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *bulkDeleteUserDevices({ payload }, { call, put, select }) {
      try {
        const { ids } = payload;
        if (!ids || ids.length === 0) return;

        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Delete,
          ids,
        });

        const pageSize = yield select((state) => state.global.pageSize);
        const deviceState = yield select((state) => state.userDevices);
        const globalState = yield select((state) => state.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const params = {
          ...CoreQueries[fetchParams].paged,
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(deviceState.orderBy && { orderBy: deviceState.orderBy }),
          ...(filters && { filter: filters }),
          select: deviceState.select,
        };

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'fetchList', payload: params });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *showForm({ payload }, { put }) {
      yield put({ type: 'queryShowForm', payload });
      if (!_.isNil(payload.id)) {
        yield put({ type: 'editOrDetail', payload });
      }
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'queryOrderBy', payload });
    },

    *setPaginationPage({ payload }, { put }) {
      yield put({ type: 'queryPaginationPage', payload });
    },

    *changeSuccess({ payload }, { put }) {
      yield put({ type: 'success', payload });
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },
  },

  reducers: {
    queryData(state, { payload }) {
      const items = payload.items ?? payload.data ?? state.list;
      return {
        ...state,
        list: items,
        pagination: _.merge(state.pagination, toPaginate(payload)),
      };
    },

    queryItem(state, { payload }) {
      return {
        ...state,
        item: payload,
      };
    },

    queryShowForm(state, { payload }) {
      return {
        ...state,
        showForm: payload.state,
        detail: payload.detail,
        id: payload.id,
        item: payload.item ?? null,
        success: false,
      };
    },

    editOrDetail(state, { payload }) {
      return {
        ...state,
        item: payload.item ?? {},
      };
    },

    queryOrderBy(state, { payload }) {
      return {
        ...state,
        orderBy: payload,
      };
    },

    queryPaginationPage(state, { payload }) {
      return {
        ...state,
        pagination: { ...state.pagination, page: payload },
      };
    },

    success(state, { payload }) {
      return {
        ...state,
        ...payload,
        success: payload?.success ?? payload,
      };
    },

    reset() {
      return { ...initialState };
    },
  },
};

export default UserDevicesModel;
